import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CarFront, Check, Users } from "lucide-react";
import { Field, PageHeader, PrimaryBtn, inputCls } from "@/components/Common";
import { BookingDialog } from "@/components/BookingDialog";
import { cabFare, http, inr, today } from "@/lib/api";

const IMG = "https://images.unsplash.com/photo-1558187424-f786111643b0?auto=format&fit=crop&w=1600&q=80";
const TRIPS = { airport: "Airport transfer", local: "Local rental · 8h / 80km", outstation: "Outstation" };

export default function Cabs() {
  const [sp] = useSearchParams();
  const [q, setQ] = useState({ trip: sp.get("trip") || "airport", pickup: sp.get("pickup") || "", drop: "", date: sp.get("date") || today(3), km: 250, round: false });
  const [selected, setSelected] = useState(null);
  const { data = [] } = useQuery({ queryKey: ["cabs"], queryFn: () => http.get("/cabs").then((r) => r.data) });
  const set = (k) => (e) => setQ({ ...q, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const ready = q.pickup.trim().length > 1 && (q.trip !== "outstation" || q.drop.trim().length > 1);

  return (
    <div data-testid="cabs-page">
      <PageHeader eyebrow="Taxi & cabs" title="A driver, wherever you land." sub="Airport pickups, full-day city rentals and outstation trips with tolls & driver allowance shown upfront." image={IMG} />
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <div className="-mt-10 relative rounded-2xl bg-white p-5 shadow-xl">
          <div className="flex flex-wrap gap-2 mb-4">
            {Object.entries(TRIPS).map(([k, l]) => (
              <button key={k} onClick={() => setQ({ ...q, trip: k })} data-testid={`cab-trip-${k}`}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${q.trip === k ? "bg-[#0D1B2A] text-white" : "bg-[#F4F0EA] text-slate-600"}`}>{l}</button>
            ))}
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 items-end">
            <Field label="Pickup"><input className={inputCls} value={q.pickup} onChange={set("pickup")} placeholder="Address or airport" data-testid="cab-pickup-input" /></Field>
            <Field label={q.trip === "outstation" ? "Destination city" : "Drop (optional)"}><input className={inputCls} value={q.drop} onChange={set("drop")} placeholder="e.g. Agra" data-testid="cab-drop-input" /></Field>
            <Field label="Pickup date"><input type="date" min={today()} className={inputCls} value={q.date} onChange={set("date")} data-testid="cab-date-input" /></Field>
            {q.trip === "outstation" && (
              <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
                <Field label="Approx. distance (km)"><input type="number" min={1} className={inputCls} value={q.km} onChange={set("km")} data-testid="cab-km-input" /></Field>
                <label className="flex h-11 items-center gap-2 text-sm"><input type="checkbox" checked={q.round} onChange={set("round")} data-testid="cab-round-checkbox" />Round trip</label>
              </div>
            )}
          </div>
        </div>

        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {data.map((c, i) => (
            <div key={c.id} className="lift rounded-2xl border border-[#E5E0D8] bg-white p-6 fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`cab-card-${c.name.toLowerCase().replace(/\s/g, "-")}`}>
              <div className="flex items-center justify-between">
                <span className="grid h-12 w-12 place-items-center rounded-xl bg-[#F4F0EA]"><CarFront className="h-6 w-6 text-[#E07A5F]" /></span>
                <span className="flex items-center gap-1 text-sm text-slate-500"><Users className="h-4 w-4" />{c.seats} seats</span>
              </div>
              <h3 className="font-display mt-5 text-2xl font-bold">{c.name}</h3>
              <p className="text-sm text-slate-500">{c.model}</p>
              <ul className="mt-4 space-y-1.5 text-sm text-slate-600">
                {["Fuel & driver allowance included", q.trip === "outstation" ? `₹${c.rate_per_km}/km · tolls extra at actuals` : "Tolls & parking at actuals", "Free cancellation up to 6h before"].map((t) => (
                  <li key={t} className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-600" />{t}</li>
                ))}
              </ul>
              <div className="mt-5 flex items-end justify-between border-t border-[#E5E0D8] pt-4">
                <p className="text-2xl font-bold" data-testid={`cab-fare-${c.name.toLowerCase().replace(/\s/g, "-")}`}>{inr(cabFare(c, q.trip, q.km, q.round))}</p>
                <PrimaryBtn className="h-10 px-5" disabled={!ready} onClick={() => setSelected(c)} data-testid={`cab-book-btn-${c.name.toLowerCase().replace(/\s/g, "-")}`}>Book cab</PrimaryBtn>
              </div>
            </div>
          ))}
        </div>
        {!ready && <p className="mt-4 text-sm text-slate-500" data-testid="cab-hint">Enter pickup{q.trip === "outstation" ? " and destination" : ""} to book.</p>}
      </div>
      {selected && (
        <BookingDialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)} title={`${selected.name} · ${TRIPS[q.trip]}`}
          amount={cabFare(selected, q.trip, q.km, q.round)}
          lines={[["Pickup", q.pickup], ...(q.drop ? [["Drop", q.drop]] : []), ["Date", q.date], ...(q.trip === "outstation" ? [["Distance", `${q.km} km${q.round ? " × 2" : ""}`]] : [])]}
          booking={{ type: "cab", item_id: selected.id, travel_date: q.date, trip_type: q.trip, pickup: q.pickup, drop: q.drop, distance_km: +q.km || 0, round_trip: q.round, travellers: 1 }} />
      )}
    </div>
  );
}
